import * as React from "react";
import { Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";

import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";

export interface InfoField {
  label: string;
  value: string | React.ReactNode;
  type?: 'text' | 'tags' | 'date' | 'status' | 'boolean';
}

export interface InfoCardProps {
  title: string;
  subtitle?: string;
  fields: InfoField[];
  isLoading?: boolean;
}

export function InfoCard({
  title,
  subtitle,
  fields,
  isLoading
}: InfoCardProps): React.JSX.Element {
  const renderFieldValue = (field: InfoField) => {
    if (isLoading) {
      return (
        <CustomSkeleton 
          variant="text" 
          width={field.type === 'tags' ? 120 : 80} 
          height={field.type === 'tags' ? 26 : 22} 
        />
      );
    }

    switch (field.type) {
      case 'tags': {
        const tags = Array.isArray(field.value) ? field.value : [field.value];
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {tags.map((tag, index) => (
              <Chip 
                key={index} 
                label={tag} 
                size="small" 
                variant="outlined"
                color="primary"
                sx={{ 
                  fontSize: '0.75rem',
                  height: 24,
                  '& .MuiChip-label': {
                    px: 1
                  }
                }} 
              />
            ))}
          </Box>
        );
      }
      case 'status': {
        const statusConfig = {
          active: { label: 'Active', color: 'success' as const },
          inactive: { label: 'Inactive', color: 'error' as const },
          pending: { label: 'Pending', color: 'warning' as const },
          completed: { label: 'Completed', color: 'info' as const },
        };
        
        const config = statusConfig[field.value as keyof typeof statusConfig] || 
                       { label: field.value, color: 'default' as const };
        
        return (
          <Chip 
            label={config.label} 
            size="small" 
            color={config.color} 
            variant="filled"
            sx={{ fontSize: '0.75rem', height: 24 }}
          />
        );
      }
      default: {
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {field.value}
          </Typography>
        );
      }
    }
  };

  return (
    <Card sx={{ height: 'max-content' }}>
      <CardContent sx={{ p: 3, pb: '16px !important' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            {
              title
            }
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {
                subtitle
              }
            </Typography>
          )}
        </Box>

        <Grid container spacing={2}>
          {fields.map((field, index) => (
            <Grid key={index} size={{ xs: 12, md: 6 }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                fontWeight={600}
                sx={{ 
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em',
                  
                }}
              >
                {field.label}
              </Typography>
              {renderFieldValue(field)}
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}
